import React, { useState, useRef } from "react";
import { useDispatch } from "react-redux";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CancelIcon from '@mui/icons-material/Cancel';
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Table from '../helpers/Table'
import DeletePopup from "../helpers/DeletePopup";

import { addDriver, editDriver, deleteDriver } from "../../actions/valueActions";

import { convertBoolDiffValue } from "./valueFunctions";
import firstCharToUpper from "../helpers/stringFormat";
import CostValueDriverCSForm from "./CostValueDriverCSForm";
import { getIconFromDriverName } from "./iconFunctions";
import { getAllOutcomes } from "./valueFunctions";

const CostValueDriverCS = ({ id, valueModel, costs, driver, outcome }) => {
    const formikRef = useRef(null);
    const dispatch = useDispatch();

    // const [open, setOpen] = React.useState(false);
    const [deleteCSDriverPopupOpen, setDeleteCSDriverPopupOpen] = useState(false);
    const [deleteCSDriverId, setDeleteCSDriverId] = useState(null);
    const [editCSDriverId, setEditCSDriverId] = useState(null);

    const openDeleteCSDriver = (id) => {
        setDeleteCSDriverId(id)
        setDeleteCSDriverPopupOpen(true)
    }

    const editCSDriver = (id) => {
        setEditCSDriverId(id)
    }

    const onSubmitCS = async (values, { setSubmitting, setErrors }) => {
        // Clone the values object to avoid direct mutation
        const clonedValues = { ...values };
        clonedValues.id = editCSDriverId;
        if (outcome) {
            clonedValues.outcome = outcome;
        }
        clonedValues.compensation = clonedValues.compensation.toString().replace(/,/g, "");

        let success = false

        if (editCSDriverId) {
            const result = await dispatch(editDriver('cs', id, clonedValues, { setSubmitting, setErrors }))
            success = result?.success;
            if (success) {
                setEditCSDriverId(null)
            }
        }
        else {
            const result = await dispatch(addDriver('cs', id, clonedValues, { setSubmitting, setErrors }))
            success = result?.success;
        }

        if (formikRef.current && success) {
            formikRef.current.resetForm();
        }
    }

    const handleResetCS = () => {
        setEditCSDriverId(null)

        // Access the formikRef current value and call resetForm
        if (formikRef.current) {
            formikRef.current.resetForm();
        }
    };

    const csDriverColumns = [
        { id: 'outcome', label: 'Outcome', minWidth: 170, action: false },
        { id: 'driver_name', label: 'Name', minWidth: 170, action: false },
        // { id: 'share_contacting', label: '% of customers contacting', format: (value) => value + '%', minWidth: 100, action: false },
        // { id: 'contact_rate', label: 'Contact rate', format: (value) => value + ' contacts', minWidth: 100, action: false },
        { id: 'contact_decrease', label: 'Contact', minWidth: 100, action: false },
        { id: 'efficiency_gain_contact', label: 'Efficiency', minWidth: 100, format: (value) => createEfficiencyChip(value), action: false },
        { id: 'positive_driver', label: 'Differentiation value', minWidth: 100, format: (value) => convertBoolDiffValue(Boolean(value)), action: false },
        { id: 'actions', label: 'Actions', minWidth: 100, action: false },
    ];

    const createCSDriverData = (id, outcome, driver_name, share_contacting, contact_rate, contact_decrease, efficiency_gain_contact, positive_driver, actions) => {
        contact_decrease = `${contact_decrease}% ${positive_driver ? 'decrease' : 'increase'}`;

        // Create a shallow copy of efficiency_gain_contact to avoid mutating the original object
        const efficiencyGainContactCopy = { ...efficiency_gain_contact };

        // Modify the copy
        Object.keys(efficiencyGainContactCopy).forEach(type => {
            efficiencyGainContactCopy[type] = `${efficiencyGainContactCopy[type]}% ${positive_driver ? 'gain' : 'loss'}`;
        });

        return {
            id,
            outcome,
            driver_name,
            share_contacting,
            contact_rate,
            contact_decrease,
            efficiency_gain_contact: efficiencyGainContactCopy,
            positive_driver,
            actions
        };
    };

    const csDriverActions = (id) => {
        return (
            <React.Fragment>
                <IconButton size="small" onClick={() => editCSDriver(id)}>
                    <EditIcon />
                </IconButton>
                <IconButton size="small" onClick={() => openDeleteCSDriver(id)}>
                    <DeleteIcon />
                </IconButton>
            </React.Fragment>
        )
    }

    const createEfficiencyChip = (values) => {
        return (
            <React.Fragment>
                {
                    Object.entries(values).map(([key, value]) => {
                        if (!value.startsWith('0%')) {
                            return (
                                <Chip key={key} label={firstCharToUpper(key) + ': ' + value} sx={{ mr: 1, mb: 0.5 }} />
                            )
                        }
                        else {
                            return null;
                        }
                    })
                }
            </React.Fragment>
        )
    }

    const csDriverRows = valueModel.cost_value_driver.cs_savings.filter(driver => !outcome || driver.outcome === outcome).map((driver) => {
        return (
            createCSDriverData(driver.id, driver.outcome, driver.driver_name, driver.share_contacting, driver.contact_rate, driver.contact_decrease, driver.efficiency_gain_contact, driver.positive_driver, csDriverActions(driver.id))
        )
    })

    const csDriverTable = () => {
        return (
            <React.Fragment>
                <Divider />
                <Table
                    columns={csDriverColumns}
                    rows={csDriverRows}
                    selectedId={editCSDriverId}
                />
            </React.Fragment>
        )
    }

    const CSDriverFormActions = () => {
        return (
            <React.Fragment>
                {editCSDriverId ? null
                    : <Button variant="contained" startIcon={<Box component={getIconFromDriverName(driver)} />} size="small" type="submit">Add driver</Button>
                }
                {editCSDriverId ?
                    <Stack direction="row" spacing={2}>
                        <Button variant="contained" startIcon={<EditIcon />} size="small" type="submit">Save</Button>
                        <Button variant="contained" startIcon={<CancelIcon />} size="small" onClick={handleResetCS}>Cancel</Button>
                    </Stack>
                    : null
                }
            </React.Fragment>
        )
    }

    return (
        <Box>
            <Divider sx={{ mb: 3 }} />
            <Stack gap={1} direction="row" sx={{ ml: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                    Driver type:
                </Typography>
                <Typography variant="caption">
                    Customer Support Savings
                </Typography>
            </Stack>

            <CostValueDriverCSForm
                currency="€"
                outcome={outcome}
                outcomeList={getAllOutcomes(valueModel?.driver_analysis)}
                product={valueModel.product}
                costStructure={costs["personnel_costs"]}
                editCSDriverId={editCSDriverId}
                editCSDriver={valueModel.cost_value_driver.cs_savings.find(csDriver => csDriver.id === editCSDriverId)}
                formActions={CSDriverFormActions}
                onSubmit={(values, { setSubmitting, setErrors }) => onSubmitCS(values, { setSubmitting, setErrors })}
                ref={formikRef}
            />
            {csDriverTable()}
            <DeletePopup open={deleteCSDriverPopupOpen} onClose={() => setDeleteCSDriverPopupOpen(false)} onDelete={() => dispatch(deleteDriver('cs', id, deleteCSDriverId))} />
        </Box>
    )

}

export default CostValueDriverCS